import { useEffect, useState, useCallback } from 'react';

/**
 * useChantierMenu — global chantier "cart" backed by localStorage.
 *
 * Lets the user browse the 15 chantiers on /prsnl, tap "Ajouter au menu"
 * on the ones that resonate, then land on /configurer with those chantiers
 * already selected (format = distanciel).
 *
 * Uses a custom 'chantier-menu-change' event to keep multiple listeners
 * in sync inside the same tab (localStorage 'storage' event only fires
 * across tabs).
 */
const STORAGE_KEY = 'sabril.chantierMenu.v1';
const EVENT = 'chantier-menu-change';

const read = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : [];
  } catch (_) {
    return [];
  }
};

const write = (arr) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
    window.dispatchEvent(new CustomEvent(EVENT, { detail: arr }));
  } catch (_) {
    /* quota / private mode — silent */
  }
};

/** Read-and-clear helper used by ConfiguratorPage on mount. */
export const consumeChantierMenu = () => {
  const items = read();
  if (items.length) {
    try { localStorage.removeItem(STORAGE_KEY); } catch (_) { /* noop */ }
    window.dispatchEvent(new CustomEvent(EVENT, { detail: [] }));
  }
  return items;
};

export default function useChantierMenu() {
  const [items, setItems] = useState(read);

  useEffect(() => {
    const sync = () => setItems(read());
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const has = useCallback((n) => items.includes(n), [items]);

  const add = useCallback((n) => {
    const current = read();
    if (current.includes(n)) return;
    write([...current, n]);
  }, []);

  const remove = useCallback((n) => {
    const current = read();
    if (!current.includes(n)) return;
    write(current.filter((x) => x !== n));
  }, []);

  const toggle = useCallback((n) => {
    const current = read();
    if (current.includes(n)) write(current.filter((x) => x !== n));
    else write([...current, n]);
  }, []);

  const clear = useCallback(() => write([]), []);

  return { items, count: items.length, has, add, remove, toggle, clear };
}
